import React, { useState, useEffect, useRef, useContext, useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useApiCall } from "../custom-hooks/api-call";
import { GlobalContext } from "../../context/Context";
import moment from "moment";
import { ChevronDown, MoreVertical, Paperclip, Pin, Plus } from "lucide-react";
import Avatar from "../ui/avatar";
import { CustomDropdown } from "../ui/dropdown/CustomDropdown";
import { CustomDropdownButton } from "../ui/dropdown/CustomDropdownButton";
import { CustomDropdownMenu } from "../ui/dropdown/CustomDropdownMenu";
import { CustomDropdownMenuList } from "../ui/dropdown/CustomDropdownMenuList";
import "./CommentsSection.css";
import parse from "html-react-parser";

function CommentTypeDropdown({
  commentType,
  setCommentType,
  commentTypes,
  IsVerified,
}) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!dropdownRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={dropdownRef} className="relative w-[135px] text-[12px]">
      {/* Button */}
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full px-2 py-1.5
        border border-slate-200 rounded-md bg-white
        hover:bg-slate-50 transition"
      >
        <span className="truncate capitalize">
          {commentType.replace("_", " ")}
        </span>

        <ChevronDown
          size={14}
          className={`transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {/* Dropdown Menu */}
      {open && (
        <div
          className="absolute right-0 bottom-full mb-1 w-full
  bg-white border border-slate-200 rounded-md shadow-lg
  z-50 overflow-hidden"
        >
          {commentTypes
            ?.filter((ct) => {
              if (ct?.id === commentType) return false;
              if (ct?.id === "unverified" && !IsVerified) return false;
              return true;
            })
            ?.map((ct) => (
              <button
                key={ct?.id}
                onClick={() => {
                  setCommentType(ct?.id);
                  setOpen(false);
                }}
                className="block w-full text-left px-2 py-1.5
                hover:bg-slate-100 transition truncate"
              >
                {ct?.value}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

const CommentsSection = ({
  type,
  id,
  apiBaseUrl,
  setAlertData,
  title,
  IsVerified = false,
  // commentTypes = [],
  HasPopupUi = false,
  handleVerificationChange = () => { },
  condition = "",
  data = {},
  updateCount = () => { },
}) => {

  const { state } = useContext(GlobalContext);
  const loginUserId = state?.user?.userid;
  const apiCall = useApiCall();

  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [isCommentLoading, setIsCommentLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [commentType, setCommentType] = useState("general");
  const commentsEndRef = useRef(null);

  const editorRef = useRef(null);

  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [savedRange, setSavedRange] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [taggedUsers, setTaggedUsers] = useState([]);

  const commentsContainerRef = useRef(null);
  const [showScrollBtn, setShowScrollBtn] = useState(false);

  const [pinnedCommentId, setPinnedCommentId] = useState(null);

  useEffect(() => {
    const pinned = comments.find((c) => c.is_pinned === 1);

    if (pinned) {
      setPinnedCommentId(pinned.id);
    }
  }, [comments]);

  const [showPinned, setShowPinned] = useState(true);
  const commentRefs = useRef({});

  const togglePin = async (commentId) => {
    try {
      const res = await apiCall({
        method: "post",
        url: `pin-comment/${commentId}`,
      });

      if (res?.success) {
        // update pinnedCommentId from API
        setPinnedCommentId(res.is_pinned ? res.comment_id : null);

        // update comments array locally
        setComments((prev) =>
          prev.map((c) => ({
            ...c,
            is_pinned: c.id === res.comment_id ? (res.is_pinned ? 1 : 0) : 0,
          })),
        );
      }
    } catch (error) {
      console.error("Error pinning comment:", error);
    }
  };

  const pinnedComment = useMemo(
    () => comments.find((c) => c.id === pinnedCommentId),
    [comments, pinnedCommentId],
  );

  const normalComments = comments;

  const scrollToBottom = (smooth = true) => {
    const container = commentsContainerRef.current;
    if (!container) return;

    container.scrollTo({
      top: container.scrollHeight,
      behavior: smooth ? "smooth" : "auto",
    });
  };

  const [commentTypes, setCommentTypes] = useState([]);

  const getCommentTypes = async () => {
    try {
      const res = await apiCall({
        method: "get",
        url: "get-dropdown-list",
      });
      console.log("res?.data", res?.data);
      setCommentTypes(type == "lead" ? res?.data?.lead_comment_types : res?.data?.comment_types || []);
    } catch (error) {
      console.warn("Error fetching Package:", error);
    }
  };
  useEffect(() => {
    getCommentTypes();
  }, []);

  useEffect(() => {
    const container = commentsContainerRef.current;
    if (!container) return;

    // scroll after DOM render
    setTimeout(() => {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: "smooth",
      });
    }, 50);
  }, [comments.length]); // only length, not full array

  useEffect(() => {
    const container = commentsContainerRef.current;
    if (!container) return;

    const isNearBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight <
      120;

    if (isNearBottom) {
      scrollToBottom();
    } else {
      setShowScrollBtn(true);
    }
  }, [comments]);

  useEffect(() => {
    const container = commentsContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const isNearBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight <
        120;

      setShowScrollBtn(!isNearBottom);
    };

    container.addEventListener("scroll", handleScroll);

    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  // Fetch comments
  const GetComments = async () => {
    if (!id) return;
    try {
      setIsCommentLoading(true);
      const res = await apiCall({
        method: "get",
        url: `${apiBaseUrl}/get-${apiBaseUrl}-comments/${type}/${id}`,
      });
      setComments(res?.comments || []);

      updateCount({
        ...data,
        comments_count: res?.comments?.length,
      });
    } catch (error) {
      console.error("Error fetching comments:", error);
    } finally {
      setIsCommentLoading(false);
    }
  };

  // ✅ Upload files
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);

    try {
      const formData = new FormData();
      files.forEach((file) => formData.append("files[]", file));

      const uploadRes = await apiCall({
        method: "post",
        url: `attachments/upload`,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (Array.isArray(uploadRes?.data)) {
        const uploaded = uploadRes.data.map((f) => ({
          id: f.id,
          file_url: f.file_url,
          status: f.status,
          file_name: f.file_name,
          uploaded_by: loginUserId,
        }));
        setAttachments((prev) => [...prev, ...uploaded]);
      }
    } catch (err) {
      console.error("File upload error:", err);
      setAlertData?.({
        isShow: true,
        type: "error",
        message: "Error uploading files",
      });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const scrollToPinned = () => {
    if (!pinnedCommentId) return;

    const el = commentRefs.current[pinnedCommentId];

    if (!el) return;

    el.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });

    el.classList.add("highlight-comment");

    setTimeout(() => {
      el.classList.remove("highlight-comment");
    }, 2000);
  };

  const AddComment = async () => {
    if (sending) return;

    const editor = editorRef.current;
    if (!editor) return;

    const commentText = newComment.trim();

    if (!commentText && attachments.length === 0) return;
    setNewComment("");

    const mentions = Array.from(
      editorRef.current.querySelectorAll("span[data-id]"),
    ).map((el) => Number(el.dataset.id));

    const uniqueMentions = [...new Set(mentions)];

    const tempId = Date.now();

    const tempComment = {
      id: tempId,
      comment: commentText,
      comment_timestamp: moment().format("YYYY-MM-DD HH:mm"),
      attachments: attachments,
      comment_type: commentType,
      status: "sending",
      user: {
        name: state?.user?.name,
        userimg: state?.user?.userimg,
      },
    };

    // 🔥 1. Immediately show comment
    setComments((prev) => [...prev, tempComment]);

    scrollToBottom();
    setAttachments([]);
    editorRef.current.innerText = "";

    try {
      const res = await apiCall({
        method: "post",
        url: `${apiBaseUrl}/add-${apiBaseUrl}-comment`,
        data: {
          [`${type}_id`]: id,
          comment: commentText,
          attachment_ids: attachments.map((a) => a.id),
          comment_type: commentType,
          mention_ids: uniqueMentions,
        },
      });

      if (res?.status === true) {
        setComments((prev) =>
          prev.map((c) =>
            c.id === tempId
              ? {
                ...c,
                id: res?.data?.id, // real id from API
                status: "sent",
              }
              : c
          )
        );
      }
    } catch (error) {
      console.error("Comment failed", error);

      // ❌ mark as failed
      setComments((prev) =>
        prev.map((c) => (c.id === tempId ? { ...c, status: "failed" } : c)),
      );
    }
  };

  const handleTextareaInput = (e) => {
    const el = e.target;
    el.style.height = "auto";
    el.style.height = el.scrollHeight + "px";
  };

  useEffect(() => {
    if (id) GetComments();
  }, [id]);

  // Example members list (replace with your actual members)
  const members = [
    { id: 1, name: "Alice ALex" },
    { id: 2, name: "John" },
    { id: 3, name: "Harlay" },
    { id: 4, name: "Zam" },
    { id: 5, name: "Jimmy" },
  ];

  const handleEditorInput = () => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    const node = range.startContainer;

    if (node.nodeType !== Node.TEXT_NODE) {
      setShowMentions(false);
      return;
    }

    const text = node.textContent || "";
    const cursor = range.startOffset;

    const before = text.slice(0, cursor);
    const lastAt = before.lastIndexOf("@");

    if (lastAt === -1) {
      setShowMentions(false);
      return;
    }

    if (lastAt > 0 && before[lastAt - 1] !== " ") {
      setShowMentions(false);
      return;
    }

    const query = before.slice(lastAt + 1);

    if ((query.match(/ /g) || []).length >= 3) {
      setShowMentions(false);
      return;
    }

    if (query.includes("\n")) {
      setShowMentions(false);
      return;
    }

    setMentionQuery(query);
    setShowMentions(true);
    setSavedRange(range.cloneRange());
    setActiveIndex(0); // reset active index
  };

  const hasContent = () => {
    const editor = editorRef.current;
    if (!editor) return false;

    // Text content
    const text = editor.textContent?.replace(/\u00A0/g, " ").trim() || "";

    // Mentions
    const mentions = editor.querySelectorAll("span[data-id]");

    return text.length > 0 || mentions.length > 0 || attachments.length > 0;
  };

  const [isNotDisable, setIsNotDisable] = useState(hasContent());

  const handleKeyDown = (e) => {
    // ✅ SHIFT + ENTER → New line
    setIsNotDisable(hasContent());
    if (e.key === "Enter" && e.shiftKey) return;

    if (e.key === "Enter") {
      e.preventDefault();
      const editor = editorRef.current;
      // const text = editor?.innerText?.trim();
      const text = editor?.textContent?.replace(/\u00A0/g, " ").trim();

      // ✅ If editor empty → do nothing
      if (!text) return;

      // ✅ If mention dropdown open AND users exist → select mention
      if (showMentions && filteredMembers.length > 0) {
        const user = filteredMembers[activeIndex];
        if (user) {
          insertMention(user);
          return;
        }
      }

      // ✅ Otherwise send comment
      AddComment();
      return;
    }

    // Below only works when mention dropdown is open
    if (!showMentions || filteredMembers.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) =>
        prev + 1 >= filteredMembers.length ? 0 : prev + 1,
      );
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) =>
        prev - 1 < 0 ? filteredMembers.length - 1 : prev - 1,
      );
    }

    if (e.key === "Escape") {
      e.preventDefault();
      setShowMentions(false);
    }
  };

  const insertMention = (user) => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    let range = selection.getRangeAt(0);

    if (savedRange) {
      range = savedRange.cloneRange();
      selection.removeAllRanges();
      selection.addRange(range);
    }

    const node = range.startContainer;
    if (node.nodeType !== Node.TEXT_NODE) return;

    const text = node.textContent || "";
    const cursor = range.startOffset;

    const before = text.slice(0, cursor);
    const lastAt = before.lastIndexOf("@");

    if (lastAt === -1) return;

    range.setStart(node, lastAt);
    range.setEnd(node, cursor);
    range.deleteContents();

    const span = document.createElement("span");
    span.textContent = `@${user.name}`;
    span.className = "mention text-blue-600 font-medium";
    span.contentEditable = "false";
    span.dataset.id = user.userid;

    const space = document.createTextNode(" ");

    range.insertNode(space);
    range.insertNode(span);

    range.setStartAfter(space);
    range.collapse(true);

    selection.removeAllRanges();
    selection.addRange(range);

    setTaggedUsers((prev) =>
      prev.some((u) => u.id === user.id) ? prev : [...prev, user],
    );

    setShowMentions(false);
    setMentionQuery("");
    editorRef.current.focus();
  };

  const [mentionList, setMentionList] = useState([]);
  const [loadingMention, setLoadingMention] = useState(false);
  const filteredMembers = mentionList?.filter((m) =>
    m.name.toLowerCase().includes(mentionQuery.toLowerCase()),
  );
  const getMentionUsers = async () => {
    if (loadingMention) return;
    setLoadingMention(true);

    try {
      const res = await apiCall({
        method: "get",
        url: `users/agents-list`,
      });

      console.log("Users API Response ===>", res);
      const newUsers = res?.agents || res?.customers || res?.data || [];
      setMentionList(res?.agents || []);
    } catch (err) {
      console.error("Error fetching users", err);
    } finally {
      setLoadingMention(false);
    }
  };

  useEffect(() => {
    getMentionUsers();
  }, []);

  const tagStyles = {
    general: "bg-[#8b5cf61a] text-[#5b21b6]",
    scheduled: "bg-[#dbe0f5] text-[#335cff]",
    unverified: "bg-[#881337] text-[#f43f5e1a]",
    verify: "bg-[#e8f5e9] text-[#388e3c]",
    no_answer: "bg-[#f3f3f3] text-[#6b7280]",
    call_later: "bg-[#fef3c7] text-[#d97706]",
    language_barrier: "bg-[#fef2f2] text-[#b91c1c]",
    invalid_lead: "bg-[#fce7f3] text-[#9d174d]",
    not_interested: "bg-[#ede9fe] text-[#6d28d9]",
    paid: "bg-[#e6ffed] text-[#059669]",
    upsell_action: "bg-[#fff7ed] text-[#d97706]",
    sms: "bg-[#eff6ff] text-[#2563eb]",
  };


  const highlightMentions = (text, mentionList) => {
    if (!text) return null;

    // If no mentions, just parse HTML
    if (!mentionList?.length) return parse(text);

    // Escape special regex characters in names
    const escapedNames = mentionList.map((user) =>
      user.name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    );

    // Create regex for mentions
    const regex = new RegExp(`(@(${escapedNames.join("|")}))`, "g");

    const parts = text.split(regex);

    return parts.map((part, index) => {
      // Check if part is a mention
      if (mentionList.some((user) => `@${user.name}` === part)) {
        return (
          <span
            key={index}
            className="text-blue-700 bg-blue-100 px-2 py-0.5 rounded-md text-xs font-semibold"
          >
            {part}
          </span>
        );
      }

      // Parse any HTML in the part
      return <React.Fragment key={index}>{parse(part)}</React.Fragment>;
    });
  };

  const itemRefs = useRef([]);

  useEffect(() => {
    if (!showMentions) return;

    const activeItem = itemRefs.current[activeIndex];
    if (activeItem) {
      activeItem.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [activeIndex, showMentions]);


  return (
    <div className="relative flex flex-col rounded-[12px] overflow-auto h-full">
      {title ? (
        <>
          <div className="flex justify-between items-center  pb-3 ">
            {HasPopupUi ? (
              <>
                <h2 className="font-medium text-[22px]">
                  Activity Logs & Comments
                </h2>
              </>
            ) : null}
          </div>
        </>
      ) : null}

      {/* --- Comments List --- */}

      {pinnedComment && (
        <div
          onClick={scrollToPinned}
          className="sticky top-0 z-40 bg-yellow-50 border border-yellow-200 rounded-lg p-2 mt-1 mb-2 shadow-sm cursor-pointer"
        >
          <div className="flex justify-between items-center gap-3">
            <div className="flex gap-2 items-start flex-1">
              <Pin size={16} className="text-yellow-600" />

              <div>
                {showPinned && (
                  <p className="text-sm text-gray-700">
                    {highlightMentions(pinnedComment?.comment, mentionList)}
                  </p>
                )}
              </div>
            </div>

            <div className="flex gap-2">
              {/* unpin */}
              <button
                onClick={() => togglePin(pinnedComment.id)}
                className="text-red-500 hover:text-red-700"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}
      <div
        ref={commentsContainerRef}
        className="flex-1 flex flex-col gap-5 py-1 max-h-full overflow-y-auto scroll-smooth"
      >
        {isCommentLoading ? (
          <div className="h-full w-full flex flex-col justify-center items-center">
            <div className="text-sm text-slate-500 text-center py-6">
              Loading comments...
            </div>
          </div>
        ) : comments.length > 0 ? (
          <AnimatePresence initial={false}>
            <div className="relative">
              {/* Timeline vertical line */}
              {/* {HasPopupUi ? (
                <>
                  <div className="absolute left-[16px] top-0 bottom-0 w-[1.5px] bg-gray-200" />
                </>
              ) : null} */}
              <div
                className={`pr-1 relative ${HasPopupUi ? "space-y-6" : "space-y-3"}`}
              >
                {normalComments
                  .slice()
                  // .reverse()
                  .map((c, idx) => {
                    const commentDate = new Date(c.comment_timestamp);
                    const hasAttachments =
                      Array.isArray(c.attachments) && c.attachments.length > 0;

                    return (
                      <motion.div
                        ref={(el) => (commentRefs.current[c.id] = el)}
                        key={c.id || idx}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -5 }}
                        transition={{ duration: 0.25 }}
                        className={`flex items-start ${HasPopupUi ? "gap-3" : "gap-[10px]"} relative transition-colors duration-500`}
                      >
                        {HasPopupUi ? (
                          <>
                            {/* --- Avatar --- */}
                            <div className="relative z-5">
                              <div className="w-8 h-8 flex items-center justify-center rounded-[8px] cursor-pointer bg-[#6366f115] overflow-hidden">
                                <p className="capitalize font-semibold text-base text-[rgb(99,102,241)]">
                                  {(c.user?.name?.split(" ") || ["U"])
                                    .map((n) => n.charAt(0))
                                    .join("")}
                                </p>
                              </div>
                            </div>

                            {/* --- Comment Card --- */}
                            <div className="flex-1 rounded-lg pt-2 cursor-pointer transition-all">
                              {type == "contact" && condition == "contact" ? (
                                <div className="flex justify-between items-start">
                                  <div>
                                    <div className="flex gap-2 items-center">
                                      <p className="text-[13px] font-medium text-black flex items-center gap-2">
                                        {c?.user?.name}
                                      </p>

                                      <span
                                        className={`px-2.5 py-1 text-xs font-medium rounded-full capitalize ${tagStyles[c?.comment_type] ||
                                          tagStyles.general
                                          }`}
                                      >
                                        {c?.comment_type}
                                      </span>
                                    </div>

                                    <div className="flex items-center gap-2 text-[12px] text-gray-500">
                                      <span>{c?.comment_timestamp}</span>

                                      {c?.status === "sending" && (
                                        <span className="text-gray-400 text-xs">
                                          Sending...
                                        </span>
                                      )}

                                      {c?.status === "failed" && (
                                        <span className="text-red-500 text-xs font-medium">
                                          Failed to send
                                        </span>
                                      )}
                                    </div>
                                  </div>

                                  {/* <button
                                    onClick={() => togglePin(c.id)}
                                    className={`transition ${pinnedCommentId === c.id
                                      ? "text-yellow-600"
                                      : "text-gray-400 hover:text-yellow-600"
                                      }`}
                                  >
                                    <Pin size={14} />
                                  </button> */}

                                  <CustomDropdown position="bottom-right">
                                    <CustomDropdownButton className="p-1 text-gray-400 hover:text-gray-700">
                                      <MoreVertical size={16} />
                                    </CustomDropdownButton>

                                    <CustomDropdownMenu>
                                      <CustomDropdownMenuList
                                        onClick={() => togglePin(c.id)}
                                      >
                                        {pinnedCommentId === c.id
                                          ? "Unpin Comment"
                                          : "Pin Comment"}
                                      </CustomDropdownMenuList>
                                    </CustomDropdownMenu>
                                  </CustomDropdown>
                                </div>
                              ) : (
                                <div className="flex justify-between items-center">
                                  <p className="text-[13px] font-medium text-black flex items-center gap-2">
                                    {c?.user?.name}

                                    <span className="">
                                      {c?.is_pinned ? (
                                        <Badge
                                          color="#f59e0b"
                                          style={{
                                            fontSize: 9,
                                            padding: "1px 6px",
                                          }}
                                        >
                                          Pinned
                                        </Badge>
                                      ) : null}
                                    </span>

                                    <div className="flex items-center gap-2 text-[12px] text-gray-500">
                                      <span>{c?.comment_timestamp}</span>

                                      {c?.status === "sending" && (
                                        <span className="text-gray-400 text-xs">
                                          Sending...
                                        </span>
                                      )}

                                      {c?.status === "failed" && (
                                        <span className="text-red-500 text-xs font-medium">
                                          Failed to send
                                        </span>
                                      )}
                                    </div>
                                  </p>

                                  {/* <button
                                    onClick={() => togglePin(c.id)}
                                    className={`transition ${pinnedCommentId === c.id
                                      ? "text-yellow-600"
                                      : "text-gray-400 hover:text-yellow-600"
                                      }`}
                                  >
                                    <Pin size={14} />
                                  </button> */}

                                  <CustomDropdown position="bottom-right">
                                    <CustomDropdownButton
                                      arrow={false}
                                      className="p-1 text-gray-400 hover:text-gray-700 !border-0"
                                    >
                                      <MoreVertical size={16} />
                                    </CustomDropdownButton>

                                    <CustomDropdownMenu>
                                      <CustomDropdownMenuList
                                        onClick={() => togglePin(c.id)}
                                      >
                                        {pinnedCommentId === c.id
                                          ? "Unpin Comment"
                                          : "Pin Comment"}
                                      </CustomDropdownMenuList>
                                    </CustomDropdownMenu>
                                  </CustomDropdown>
                                </div>
                              )}

                              <p className="text-[15px] text-gray-700 mt-3 leading-snug">
                                {/* {c?.comment} */}
                                {highlightMentions(c?.comment, mentionList)}
                              </p>

                              {/* --- Attachments --- */}
                              {hasAttachments && (
                                <div className="mt-3 flex flex-wrap gap-2">
                                  {c.attachments.map((file, i) => {
                                    const fileUrl = file.file_url;
                                    const fileName =
                                      file.file_name || "Attachment";
                                    const ext = fileName
                                      .split(".")
                                      .pop()
                                      .toLowerCase();
                                    const isImage = [
                                      "jpg",
                                      "jpeg",
                                      "png",
                                      "gif",
                                      "webp",
                                    ].includes(ext);
                                    const iconMap = {
                                      pdf: "📄",
                                      doc: "📝",
                                      docx: "📝",
                                      xls: "📊",
                                      xlsx: "📊",
                                      csv: "📈",
                                      txt: "📘",
                                      zip: "🗜️",
                                      rar: "🗜️",
                                    };
                                    const fileIcon = iconMap[ext] || "📎";

                                    return (
                                      <div
                                        key={i}
                                        className="group flex items-center gap-2 border border-slate-200 bg-white hover:bg-slate-100 rounded-lg px-3 py-1 text-sm shadow-sm transition relative"
                                      >
                                        <a
                                          href={fileUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="flex items-center gap-2"
                                        >
                                          {isImage ? (
                                            <img
                                              src={fileUrl}
                                              alt={fileName}
                                              className="w-8 h-8 object-cover rounded-md border border-slate-200"
                                            />
                                          ) : (
                                            <div className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 bg-slate-100 text-lg">
                                              {fileIcon}
                                            </div>
                                          )}
                                          <span className="truncate max-w-[160px] text-slate-700 group-hover:underline">
                                            {fileName}
                                          </span>
                                        </a>

                                        {/* {file.uploaded_by === loginUserId && (
                                  <button
                                    onClick={() => handleDeleteAttachment(file.id, c.id)}
                                    className="absolute -top-2 -right-2 bg-white border border-slate-300 text-slate-400 hover:text-red-500 rounded-full w-5 h-5 flex items-center justify-center shadow-sm text-xs"
                                    title="Delete Attachment"
                                  >
                                    ✕
                                  </button>
                                )} */}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="flex flex-col w-full">
                              <Card
                                key={idx}
                                style={{
                                  width: "100%",
                                  padding: "14px 18px",
                                  borderLeft: c?.is_pinned
                                    ? "3px solid #f59e0b"
                                    : "1px solid rgba(0, 0, 0, 0.08)",
                                }}
                              >
                                <div
                                  style={{
                                    width: "100%",
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 10,
                                  }}
                                >
                                  <div
                                    style={{
                                      width: 30,
                                      height: 30,
                                      borderRadius: 8,
                                      background: T.accent + "15",
                                      color: T.accent,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontSize: 11,
                                      fontWeight: 700,
                                      flexShrink: 0,
                                    }}
                                  >
                                    {c?.user?.name
                                      .split(" ")
                                      .map((w) => w[0])
                                      .join("")}
                                  </div>

                                  <div className="flex items-center justify-between w-full">
                                    <div style={{ flex: 1 }}>
                                      <div className="flex justify-between items-center">
                                        <div
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 8,
                                          }}
                                        >
                                          <span
                                            style={{
                                              fontSize: 12,
                                              fontWeight: 600,
                                            }}
                                          >
                                            {c?.user?.name}
                                          </span>
                                          {c?.is_pinned ? (
                                            <Badge
                                              color="#f59e0b"
                                              style={{
                                                fontSize: 9,
                                                padding: "1px 6px",
                                              }}
                                            >
                                              Pinned
                                            </Badge>
                                          ) : null}

                                          <div
                                            className="flex items-center gap-2 text-[12px] text-gray-500"
                                            style={{
                                              fontSize: 11,
                                              color: T.textMuted,
                                            }}
                                          >
                                            <span>{c?.comment_timestamp}</span>

                                            {c?.status === "sending" && (
                                              <span className="text-gray-400 text-xs">
                                                Sending...
                                              </span>
                                            )}

                                            {c?.status === "failed" && (
                                              <span className="text-red-500 text-xs font-medium">
                                                Failed to send
                                              </span>
                                            )}
                                          </div>
                                        </div>

                                        {/* {type == "contact" &&
                                          condition == "contact" ? (
                                          <span
                                            className={`px-[6px] py-2 text-[10px] font-medium rounded-xl capitalize ${tagStyles[c?.comment_type] ||
                                              tagStyles.general
                                              }`}
                                          >
                                            {c?.comment_type}
                                          </span>
                                        ) : null} */}
                                        <div className="flex items-center">
                                          <span
                                            className={`px-[6px] py-2 text-[10px] font-medium rounded-xl capitalize ${tagStyles[c?.comment_type] ||
                                              tagStyles.general
                                              }`}
                                          >
                                            {c?.comment_type.split("_").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ")}
                                          </span>

                                          <CustomDropdown position="bottom-right">
                                            <CustomDropdownButton
                                              arrow={false}
                                              className="p-1 text-gray-400 hover:text-gray-700 !border-0"
                                            >
                                              <MoreVertical size={16} />
                                            </CustomDropdownButton>

                                            <CustomDropdownMenu>
                                              <CustomDropdownMenuList
                                                onClick={() => togglePin(c.id)}
                                              >
                                                {pinnedCommentId === c.id
                                                  ? "Unpin"
                                                  : "Pin"}
                                              </CustomDropdownMenuList>
                                            </CustomDropdownMenu>
                                          </CustomDropdown>
                                        </div>

                                      </div>
                                      <div
                                        style={{
                                          fontSize: 13,
                                          color: T.textSecondary,
                                          marginTop: 4,
                                          lineHeight: 1.5,
                                          whiteSpace: "break-spaces",
                                          wordBreak: "break-all",
                                        }}

                                      >
                                        {" "}
                                        {highlightMentions(
                                          c?.comment,
                                          mentionList,
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {hasAttachments && (
                                  <div className="mt-1 flex flex-wrap gap-2">
                                    {c.attachments.map((file, i) => {
                                      const fileUrl = file.file_url;
                                      const fileName =
                                        file.file_name || "Attachment";
                                      const ext = fileName
                                        .split(".")
                                        .pop()
                                        .toLowerCase();
                                      const isImage = [
                                        "jpg",
                                        "jpeg",
                                        "png",
                                        "gif",
                                        "webp",
                                      ].includes(ext);
                                      const iconMap = {
                                        pdf: "📄",
                                        doc: "📝",
                                        docx: "📝",
                                        xls: "📊",
                                        xlsx: "📊",
                                        csv: "📈",
                                        txt: "📘",
                                        zip: "🗜️",
                                        rar: "🗜️",
                                      };
                                      const fileIcon = iconMap[ext] || "📎";

                                      return (
                                        <div
                                          key={i}
                                          className="group flex items-center gap-2 border border-slate-200 bg-white hover:bg-slate-100 rounded-lg px-3 py-1 text-sm shadow-sm transition relative"
                                        >
                                          <a
                                            href={fileUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-2"
                                          >
                                            {isImage ? (
                                              <img
                                                src={fileUrl}
                                                alt={fileName}
                                                className="w-8 h-8 object-cover rounded-md border border-slate-200"
                                              />
                                            ) : (
                                              <div className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 bg-slate-100 text-lg">
                                                {fileIcon}
                                              </div>
                                            )}
                                            <span className="truncate max-w-[160px] text-slate-700 group-hover:underline">
                                              {fileName}
                                            </span>
                                          </a>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </Card>

                              {/* --- Attachments --- */}
                            </div>
                          </>
                        )}
                      </motion.div>
                    );
                  })}
              </div>
            </div>
          </AnimatePresence>
        ) : (
          <div className="text-center py-10 h-full w-full flex flex-col justify-center items-center">
            <p className="text-sm text-slate-500">💬 No comments yet.</p>
            <p className="text-xs text-slate-400 mt-1">
              Be the first to share an update!
            </p>
          </div>
        )}
        <div ref={commentsEndRef} />

        {showScrollBtn && (
          <button
            onClick={() => {
              scrollToBottom();
              setShowScrollBtn(false);
            }}
            className="absolute bottom-[99px] right-[22px]  z-50 bg-[#323232] text-white rounded-full w-5 h-5 flex items-center justify-center shadow-lg hover:bg-[#605858] transition"
          >
            <ChevronDown />
          </button>
        )}
      </div>

      {/* --- Input Section --- */}

      {showMentions && filteredMembers.length > 0 && (
        <div className="absolute max-h-[222px] top-full mt-1 w-full bg-white border    border-gray-200 rounded   z-50 overflow-auto p-1">
          {filteredMembers.map((user, idx) => (
            <div
              key={user.id}
              ref={(el) => (itemRefs.current[idx] = el)}
              // onClick={() => insertMention(user)}
              onMouseDown={(e) => {
                e.preventDefault(); // IMPORTANT to prevent focus loss
                insertMention(user);
              }}
              className={`px-3 py-2 text-sm cursor-pointer transition-all duration-200 rounded-lg ${idx === activeIndex ? "bg-slate-200" : "hover:bg-slate-100"
                }`}
            >
              @{user.name}
            </div>
          ))}
        </div>
      )}

      <div className="flex  justify-between border-t border-t-[#ccc] mt-0.2 px-3 py-2 bg-[#efefef]">
        {/* Input */}
        <textarea
          ref={editorRef}
          value={newComment}
          rows={1}
          placeholder="Write something..."
          onChange={(e) => setNewComment(e.target.value)}
          onInput={handleTextareaInput}
          onKeyDown={handleKeyDown}
          className="flex-1 bg-transparent outline-none border-none text-sm resize-none max-h-[80px] overflow-auto"
        />

        {/* Icons */}
        <div className="flex flex-col justify-between gap-3 ml-3">
          <div>
            <CommentTypeDropdown
              commentType={commentType}
              setCommentType={setCommentType}
              commentTypes={commentTypes}
              IsVerified={IsVerified}
            />
          </div>

          <div className="flex gap-3 items- justify-end">
            <div>
              {/* Attachment */}
              <input
                type="file"
                multiple
                onChange={handleFileUpload}
                id={`comment-attachments-${type}`}
                className="hidden"
              />

              <label
                htmlFor={`comment-attachments-${type}`}
                className="cursor-pointer text-[rgb(11,84,255)] hover:text-blue-600 transition"
                title="Attach files"
              >
                <Paperclip size={18} />
              </label>
            </div>

            {/* Send */}
            <button
              onClick={AddComment}
              disabled={!isNotDisable || sending}
              className="text-[rgb(11,84,255)] hover:text-blue-600 transition"
              title="Send"
            >
              <svg
                width={18}
                height={18}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="m22 2-7 20-4-9-9-4Z" />
                <path d="m22 2-11 11" />
              </svg>
            </button>
          </div>

          {/* Three dots */}
          {/* <button
            className="text-slate-500 hover:text-blue-600 transition"
            title="More"
          >
            <svg
              width={18}
              height={18}
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <circle cx="5" cy="12" r="2" />
              <circle cx="12" cy="12" r="2" />
              <circle cx="19" cy="12" r="2" />
            </svg>
          </button> */}
        </div>
      </div>

      {/* ✅ Preview selected attachments */}
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 p-2">
          {attachments.map((a, i) => {
            const fileName = a.file_name || "File";
            const fileUrl = a.file_url || a.url || "#";
            return (
              <div
                key={i}
                className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-1 text-sm shadow-sm"
              >
                <a
                  href={fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="truncate max-w-[150px] text-slate-700 hover:text-blue-600 hover:underline cursor-pointer"
                  title={fileName}
                >
                  {fileName}
                </a>
                <button
                  onClick={async () => {
                    const fileId = a.id;
                    setAttachments((prev) =>
                      prev.filter((_, idx) => idx !== i),
                    );
                    try {
                      await apiCall({
                        method: "post",
                        url: `attachments/delete-unconfirmed-attachment/${fileId}`,
                      });
                    } catch (err) {
                      console.error("Error deleting unconfirmed file:", err);
                    }
                  }}
                  className="text-slate-400 hover:text-red-500 text-xs"
                >
                  ✕
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CommentsSection;

/// DETAIL PAGE COMPONENT
const Card = ({ children, style = {}, hover = false, onClick }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => hover && setHovered(true)}
      onMouseLeave={() => hover && setHovered(false)}
      style={{
        background: T.card,
        border: `1px solid ${T.cardBorder}`,
        borderRadius: 12,
        boxShadow: hovered ? T.shadowMd : T.shadow,
        transform: hovered ? "translateY(-1px)" : "none",
        transition: "all 0.15s ease",
        cursor: onClick ? "pointer" : "default",
        ...style,
      }}
    >
      {children}
    </div>
  );
};

const Badge = ({ color, children, style = {} }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      padding: "3px 10px",
      borderRadius: 6,
      fontSize: 11,
      fontWeight: 600,
      background: color + "14",
      color,
      letterSpacing: "0.3px",
      ...style,
    }}
  >
    {children}
  </span>
);

const T = {
  bg: "#EDE8E1",
  sidebar: "#1a1730",
  sidebarHover: "#252240",
  sidebarActive: "#302d4a",
  card: "#FFFFFF",
  cardBorder: "rgba(0,0,0,0.08)",
  text: "#1a1520",
  textSecondary: "#5c5466",
  textMuted: "#908892",
  accent: "#6366f1",
  accentLight: "rgba(99,102,241,0.08)",
  accentText: "#5457e5",
  shadow: "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
  shadowMd: "0 2px 8px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)",
  shadowLg: "0 8px 30px rgba(0,0,0,0.08)",
  inputBg: "#F3EFE9",
  inputBorder: "rgba(0,0,0,0.1)",
};
